import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { toast } from "react-toastify";
import ConfirmDelete from "../../components/ConfirmDelete";
import QuillEditor from "../../components/QuillEditor";
import api from "../../utils/api";

const languages = ["en", "hy", "ru"];
const languageLabels = { en: "English", hy: "Armenian", ru: "Russian" };

const storageBase = () =>
  (process.env.REACT_APP_API_BASE_URL || "").replace(/\/api\/?$/, "") || "";

const getError = (errors, field) => {
  const v = errors[field];
  if (Array.isArray(v) && v.length) return v[0];
  if (typeof v === "string") return v;
  return "";
};

export default function AboutPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingSlide, setUploadingSlide] = useState(false);
  const [errors, setErrors] = useState({});
  const [title, setTitle] = useState({ title_hy: "", title_en: "", title_ru: "" });
  const [description, setDescription] = useState({
    description_hy: "",
    description_en: "",
    description_ru: "",
  });
  const [slides, setSlides] = useState([]);
  const descriptionEditorRefs = useRef({});

  const loadAboutPage = () => {
    return api.get("admin/content/about-page").then((res) => {
      const d = res.data?.data || {};
      const section = d.about_page || {};
      const slideList = (d.slides || []).map((s) => ({
        id: s.id,
        image: s.image,
        imageUrl: s.image ? `${storageBase()}/storage/${s.image}` : null,
      }));
      setTitle({
        title_hy: section.title_hy ?? "",
        title_en: section.title_en ?? "",
        title_ru: section.title_ru ?? "",
      });
      setDescription({
        description_hy: section.description_hy ?? "",
        description_en: section.description_en ?? "",
        description_ru: section.description_ru ?? "",
      });
      setSlides(slideList);
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAboutPage()
      .catch(() => setSlides([]))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleTitleChange = (lang, value) => {
    setTitle((prev) => ({ ...prev, [`title_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`title_${lang}`]: undefined }));
  };

  const handleDescriptionChange = (lang, value) => {
    setDescription((prev) => ({ ...prev, [`description_${lang}`]: value }));
    setErrors((prev) => ({ ...prev, [`description_${lang}`]: undefined }));
  };

  const onAddSlide = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingSlide(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await api.post("admin/content/about-page/slides", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { id, image } = res.data || {};
      const imageUrl = image ? `${storageBase()}/storage/${image}` : null;
      setSlides((prev) => [...prev, { id, image, imageUrl }]);
      toast.success("Slide image uploaded.");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to upload image.";
      toast.error(msg);
    } finally {
      setUploadingSlide(false);
    }
  };

  const deleteSlide = async (idx) => {
    const slide = slides[idx];
    if (slide.id == null) {
      setSlides((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    try {
      await api.delete(`admin/content/about-page/slides/${slide.id}`);
      setSlides((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Slide removed.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete slide.");
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    try {
      const getDesc = (lang) => {
        const ref = descriptionEditorRefs.current[`desc-${lang}`];
        if (ref && typeof ref.getContent === "function") {
          const content = ref.getContent();
          if (content !== undefined && content !== null) return content;
        }
        return description[`description_${lang}`] ?? "";
      };
      await api.put("admin/content/about-page", {
        title_hy: title.title_hy,
        title_en: title.title_en,
        title_ru: title.title_ru,
        description_hy: getDesc("hy"),
        description_en: getDesc("en"),
        description_ru: getDesc("ru"),
        slide_ids: slides.map((s) => s.id).filter(Boolean),
      });
      await loadAboutPage();
      toast.success("About page saved.");
    } catch (e) {
      if (e?.errors && typeof e.errors === "object") setErrors(e.errors);
      const message = e?.errors
        ? "Please fix the validation errors."
        : (e?.response?.data?.message || "Failed to save about page.");
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography component="h2" variant="h5" sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider", pb: 1 }}>
        About Page (public)
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        Section title, description (rich text) in 3 languages, and hero slide images shown on the About page.
      </Typography>

      <FormControl fullWidth>
        <FormLabel sx={{ display: "block", mb: 1 }}>Section title</FormLabel>
        {languages.map((lang) => (
          <Box key={lang} sx={{ mb: 1.5 }}>
            <FormLabel sx={{ display: "block", mb: 0.5, fontSize: "0.85rem" }}>{languageLabels[lang]}</FormLabel>
            <OutlinedInput
              fullWidth
              size="small"
              value={title[`title_${lang}`] || ""}
              onChange={(e) => handleTitleChange(lang, e.target.value)}
              placeholder="About us"
            />
            {getError(errors, `title_${lang}`) && (
              <FormHelperText error>{getError(errors, `title_${lang}`)}</FormHelperText>
            )}
          </Box>
        ))}
      </FormControl>

      <Box>
        <FormLabel sx={{ display: "block", mb: 1 }}>Section description (rich text)</FormLabel>
        {languages.map((lang) => (
          <Box key={lang} sx={{ mb: 2 }}>
            <FormLabel sx={{ display: "block", mb: 0.5, fontSize: "0.85rem" }}>{languageLabels[lang]}</FormLabel>
            <QuillEditor
              key={`about-desc-${lang}`}
              ref={(r) => { descriptionEditorRefs.current[`desc-${lang}`] = r; }}
              value={description[`description_${lang}`] || ""}
              onChange={(html) => handleDescriptionChange(lang, html)}
              placeholder="Description..."
            />
            {getError(errors, `description_${lang}`) && (
              <FormHelperText error>{getError(errors, `description_${lang}`)}</FormHelperText>
            )}
          </Box>
        ))}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Slide images (hero carousel)</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
          {slides.map((slide, idx) => (
            <Box
              key={slide.id ?? `new-${idx}`}
              sx={{
                position: "relative",
                width: 160,
                height: 90,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              {slide.imageUrl ? (
                <img
                  src={slide.imageUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="caption" color="text.secondary">No image</Typography>
                </Box>
              )}
              <Box sx={{ position: "absolute", top: 4, right: 4 }}>
                <ConfirmDelete
                  confirmText="Remove this slide?"
                  dialogTitle="Remove slide"
                  onConfirm={() => deleteSlide(idx)}
                >
                  <IconButton size="small" sx={{ bgcolor: "rgba(0,0,0,0.5)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ConfirmDelete>
              </Box>
            </Box>
          ))}
          <Box
            component="label"
            sx={{
              width: 160,
              height: 90,
              borderRadius: 1,
              border: "2px dashed",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
          >
            <input type="file" accept="image/*" hidden onChange={onAddSlide} disabled={uploadingSlide} />
            {uploadingSlide ? (
              <CircularProgress size={28} />
            ) : (
              <AddPhotoAlternateIcon color="action" />
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
